import { Box, Button, Divider, Flex, Heading, Icon, Text, VStack, useClipboard } from '@chakra-ui/react';
import html2canvas from 'html2canvas';
import React, { useRef } from 'react';
import { FaCopy, FaShareSquare } from 'react-icons/fa';

interface CountryPriceDetail {
  countryName: string;
  countryCode: string;
  flag: string;
  price: number;
  isMfnPrice: boolean;
}

interface ResultsCardProps {
  drugName: string;
  dosageLabel: string;
  monthlyQuantity: number;
  usMonthlyPrice: number;
  mfnMonthlyPriceOverall: number;
  monthlySavingsOverall: number;
  monthlySavingsPercentageOverall: number;
  priceDetails: CountryPriceDetail[];
}

export const ResultsCard: React.FC<ResultsCardProps> = ({
  drugName,
  dosageLabel,
  monthlyQuantity,
  usMonthlyPrice,
  mfnMonthlyPriceOverall,
  monthlySavingsOverall,
  monthlySavingsPercentageOverall,
  priceDetails,
}) => {
  const resultsRef = useRef<HTMLDivElement>(null);
  const imageCaptureRef = useRef<HTMLDivElement>(null);

  const calculatorUrl = 'https://fairsquaremedicare.com/most-favored-nation';

  // Suggested text for copying
  const suggestedPostText =
    drugName && dosageLabel && monthlyQuantity && monthlySavingsOverall > 0
      ? `Just compared prices for ${drugName} (${dosageLabel}, ${monthlyQuantity} units/month) using Fair Square Medicare's new tool!\n🇺🇸 U.S. Monthly Price: $${usMonthlyPrice.toFixed(
          2
        )} vs. Lowest Monthly Price Abroad: $${mfnMonthlyPriceOverall.toFixed(
          2
        )}.\nThat's a potential monthly saving of $${monthlySavingsOverall.toFixed(
          2
        )} (${monthlySavingsPercentageOverall.toFixed(
          1
        )}%)!\nIt's time for fair drug prices for Americans.\n\nCheck it out:\n${calculatorUrl}\n\n#FairDrugPrices #RxSavings`
      : `Check out Fair Square Medicare's new tool to see how U.S. drug prices compare to other countries!\nIt's time for fair drug prices for Americans.\n\nCheck it out:\n${calculatorUrl}\n\n#FairDrugPrices #RxSavings`;

  const { onCopy, hasCopied } = useClipboard(suggestedPostText);

  const handleShare = async () => {
    if (imageCaptureRef.current) {
      try {
        const canvas = await html2canvas(imageCaptureRef.current, {
          scale: 2,
          backgroundColor: '#ffffff',
        });
        // Convert canvas to Blob
        canvas.toBlob(function (blob) {
          if (blob) {
            // Create an object URL from the Blob
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${drugName ? drugName.toLowerCase().replace(/\s+/g, '-') + '-' : ''}price-comparison.png`;
            link.href = blobUrl;
            document.body.appendChild(link); // Append to body to ensure click works on all browsers
            link.click();
            document.body.removeChild(link); // Clean up
            URL.revokeObjectURL(blobUrl); // Release the object URL
          } else {
            console.error('Error generating image: Canvas to Blob conversion failed.');
          }
        }, 'image/png');
      } catch (error) {
        console.error('Error generating image:', error);
      }
    }
  };

  // Find the MFN country/countries for direct access if needed, though isMfnPrice flag is primary
  const mfnCountries = priceDetails.filter((detail) => detail.isMfnPrice);
  // To display a representative MFN country name if there are multiple, or for a summary line
  const mfnCountryNameDisplay =
    mfnCountries.length > 0 ? mfnCountries.map((c) => `${c.flag} ${c.countryName}`).join(', ') : 'N/A';

  // Prepare a curated list for the image (US, MFN, 1-2 others)
  const imagePriceList: CountryPriceDetail[] = [];
  const usDetail = priceDetails.find((d) => d.countryCode === 'US');
  if (usDetail) imagePriceList.push(usDetail);

  mfnCountries.forEach((mfn) => {
    if (!imagePriceList.find((d) => d.countryCode === mfn.countryCode && d.countryName === mfn.countryName)) {
      // Avoid duplicates if US is MFN
      imagePriceList.push(mfn);
    }
  });

  // Add 1 or 2 other cheapest non-US, non-MFN countries to make up to 4 total if possible
  const otherCountries = priceDetails
    .filter((d) => d.countryCode !== 'US' && !d.isMfnPrice)
    .sort((a, b) => a.price - b.price);

  let countNeeded = 4 - imagePriceList.length;
  for (let i = 0; i < Math.min(countNeeded, otherCountries.length); i++) {
    if (
      !imagePriceList.find(
        (d) => d.countryCode === otherCountries[i].countryCode && d.countryName === otherCountries[i].countryName
      )
    ) {
      imagePriceList.push(otherCountries[i]);
    }
  }
  // Ensure unique entries again due to potential overlaps if MFN was the only other country
  const uniqueImagePriceList = Array.from(new Set(imagePriceList.map((a) => a.countryCode + a.countryName))).map(
    (id) => imagePriceList.find((a) => a.countryCode + a.countryName === id)!
  );

  return (
    <>
      <Box
        ref={imageCaptureRef}
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '500px',
          height: 'auto',
          padding: '20px',
          backgroundColor: 'white',
          border: '1px solid #ccc',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <VStack spacing={4} align="stretch">
          <Heading size="lg" textAlign="center" color="blue.700">
            How much Trump's drug plan could save you for {drugName}
          </Heading>
          {dosageLabel && (
            <Text fontSize="sm" textAlign="center" color="gray.500">
              {dosageLabel} ({monthlyQuantity} units/month)
            </Text>
          )}

          <Flex justify="space-between" align="baseline" mt={2}>
            <Text fontSize="lg" color="gray.600">
              🇺🇸 U.S. Monthly Price:
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="black">
              ${usMonthlyPrice.toFixed(2)}
            </Text>
          </Flex>
          <Flex justify="space-between" align="baseline">
            <Text fontSize="lg" color="green.600">
              🌍 Lowest Monthly Price Abroad:
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="green.600">
              ${mfnMonthlyPriceOverall.toFixed(2)}
            </Text>
          </Flex>
          <Flex justify="space-between" align="baseline" bg="green.50" p={2} rounded="md">
            <Text fontSize="lg" fontWeight="bold" color="green.700">
              Your Potential Monthly Savings:
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="green.700">
              ${monthlySavingsOverall.toFixed(2)} ({monthlySavingsPercentageOverall.toFixed(1)}%)
            </Text>
          </Flex>

          <Divider my={3} />
          <Text fontSize="md" fontWeight="bold" textAlign="center">
            Example Monthly Prices (USD):
          </Text>
          <VStack spacing={1} align="stretch" mt={1}>
            {uniqueImagePriceList.slice(0, 4).map((detail) => (
              <Flex
                key={`img-${detail.countryCode}-${detail.countryName}`}
                justify="space-between"
                align="center"
                p="6px 10px"
                rounded="sm"
                bg={detail.isMfnPrice ? 'green.50' : detail.countryCode === 'US' ? 'blue.50' : 'gray.50'}
              >
                <Flex align="center">
                  <Text mr={2} fontSize="lg">
                    {detail.flag}
                  </Text>
                  <Text fontSize="md" fontWeight={detail.isMfnPrice || detail.countryCode === 'US' ? 'bold' : 'normal'}>
                    {detail.countryName}
                  </Text>
                </Flex>
                <Text fontSize="lg" fontWeight="bold">
                  ${detail.price.toFixed(2)}
                </Text>
              </Flex>
            ))}
          </VStack>

          <Divider my={3} />

          <Box textAlign="center" mt={2}>
            <Text fontSize="sm" fontWeight="bold" mb={1}>
              Scan for More Drugs & Fair U.S. Pricing:
            </Text>
            <img
              src="/email_landing/qr-code.png"
              alt="QR Code for MFN Calculator"
              width="100px"
              height="100px"
              style={{ margin: 'auto', display: 'block' }}
            />
            <Text fontSize="xs" color="gray.600" mt={1}>
              {calculatorUrl}
            </Text>
          </Box>

          <Text fontSize="xs" color="gray.500" textAlign="center" mt={4}>
            fairsquaremedicare.com
          </Text>
          <Text fontSize="2xs" color="gray.500" textAlign="center" mt={1}>
            Prices are approximate and subject to change.
          </Text>
        </VStack>
      </Box>

      <Box ref={resultsRef} bg="white" p={6} rounded="lg" shadow="lg" width="100%">
        <VStack spacing={5} align="stretch">
          {drugName && dosageLabel && (
            <Box textAlign="center" mb={2}>
              <Heading size="md" color="blue.700">
                {drugName}
              </Heading>
              <Text fontSize="sm" color="gray.600">
                {dosageLabel} ({monthlyQuantity} units/month)
              </Text>
            </Box>
          )}

          <Heading size="lg" textAlign="center" mb={4}>
            Monthly Price Comparison
          </Heading>

          {/* Summary Section - similar to old layout but using overall props */}
          <Flex justify="space-between" align="center">
            <Text color="gray.700" fontWeight="medium">
              U.S. Monthly Price (USD):
            </Text>
            <Text fontSize="xl" fontWeight="bold">
              ${usMonthlyPrice.toFixed(2)}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text color="gray.700" fontWeight="medium">
              Lowest Intl. Monthly Price (MFN):
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="green.600">
              ${mfnMonthlyPriceOverall.toFixed(2)}
            </Text>
          </Flex>
          {mfnCountries.length > 0 && (
            <Text fontSize="sm" color="gray.500" textAlign="right" mt="-3">
              (Found in: {mfnCountryNameDisplay})
            </Text>
          )}
          <Flex justify="space-between" align="center" bg="green.50" p={3} rounded="md">
            <Text color="green.700" fontWeight="bold" fontSize="lg">
              Potential Monthly Savings:
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="green.700">
              ${monthlySavingsOverall.toFixed(2)} ({monthlySavingsPercentageOverall.toFixed(1)}%)
            </Text>
          </Flex>

          <Divider pt={3} />

          {/* Detailed Country Breakdown */}
          <Heading size="md" mt={4} mb={2}>
            Monthly Price Details by Country/Region:
          </Heading>
          <VStack spacing={2} align="stretch" maxHeight="300px" overflowY="auto" pr={2}>
            {priceDetails.map((detail) => (
              <Flex
                key={detail.countryCode + detail.countryName}
                justify="space-between"
                align="center"
                p={3}
                rounded="md"
                bg={detail.isMfnPrice ? 'green.100' : detail.countryCode === 'US' ? 'blue.50' : 'gray.50'}
                border={detail.isMfnPrice ? '2px' : '1px'}
                borderColor={detail.isMfnPrice ? 'green.500' : detail.countryCode === 'US' ? 'blue.300' : 'gray.200'}
              >
                <Flex align="center">
                  <Text mr={2}>{detail.flag}</Text>
                  <Text
                    fontWeight={detail.isMfnPrice || detail.countryCode === 'US' ? 'bold' : 'normal'}
                    color={detail.isMfnPrice ? 'green.700' : detail.countryCode === 'US' ? 'blue.700' : 'gray.800'}
                  >
                    {detail.countryName}
                  </Text>
                </Flex>
                <Text
                  fontWeight="bold"
                  color={detail.isMfnPrice ? 'green.700' : detail.countryCode === 'US' ? 'blue.700' : 'gray.800'}
                >
                  ${detail.price.toFixed(2)}
                </Text>
              </Flex>
            ))}
          </VStack>

          <Divider pt={3} />
          <Button
            colorScheme="green"
            size="md"
            onClick={handleShare}
            mt={4}
            leftIcon={<Icon as={FaShareSquare} display={{ base: 'inline-block', md: 'none' }} />}
          >
            <Text display={{ base: 'none', md: 'block' }}>Share Savings (Download Image)</Text>
            <Text display={{ base: 'block', md: 'none' }}>Download</Text>
          </Button>
          <Button
            as="a"
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
              calculatorUrl
            )}&hashtag=${encodeURIComponent('#FairDrugPrices')}`}
            target="_blank"
            rel="noopener noreferrer"
            colorScheme="facebook"
            size="md"
            mt={2}
            leftIcon={
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.81C10.44 7.31 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96C18.34 21.21 22 17.06 22 12.06C22 6.53 17.5 2.04 12 2.04Z"
                />
              </svg>
            }
          >
            <Text display={{ base: 'none', md: 'block' }}>Share Page on Facebook</Text>
            <Text display={{ base: 'block', md: 'none' }}>Share on FB</Text>
          </Button>
          <Button
            onClick={onCopy}
            colorScheme={hasCopied ? 'green' : 'gray'}
            size="md"
            mt={2}
            leftIcon={<Icon as={FaCopy} />}
          >
            <Text display={{ base: 'none', md: 'block' }}>
              {hasCopied ? 'Copied Text!' : 'Copy Suggested Post Text'}
            </Text>
            <Text display={{ base: 'block', md: 'none' }}>{hasCopied ? 'Copied!' : 'Copy Text'}</Text>
          </Button>
        </VStack>
      </Box>
    </>
  );
};
